/**
 * Sentence-of-the-day save/bookmark state (R8.7, Phase 6, ADR-0002 addendum
 * 2026-07-07, "D-Sentence"). Shuffle is pure client-side selection over the
 * static `QUOTES` array (`../content/quotes.ts`) -- no event, no function
 * here; this module only answers "is quote X saved" for a caller-supplied
 * quoteId (the domain layer never tracks which quote is currently shown --
 * that is UI-local state, per the contract's own doc comment on
 * `SentenceOfDayState`).
 */

import type { ClockPort, CompassEvent, IdPort, QuoteSavedEvent, SentenceOfDayState } from '../../contracts';
import { type CommandResult, domainError } from '../event-log/command-error';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';

type Ports = { readonly clock: ClockPort; readonly id: IdPort };

/**
 * `saved` iff at least one surviving (non-reverted) `quote.saved` event
 * exists for `quoteId` -- a quote may be saved more than once across
 * different days (the event doc comment); `savedEventId` exposes the
 * EARLIEST surviving one as "saved on" (matches the event's own doc comment:
 * "the resolver dedupes by quoteId for display, keeping the earliest
 * recordedAt per quote").
 */
export function getSentenceOfDayState(
  events: readonly CompassEvent[],
  quoteId: string,
): SentenceOfDayState {
  // excludingReverted MUST run over the full log BEFORE narrowing by type --
  // see reflection.ts's identical fix/comment for why the reverse order
  // silently never sees the log_entry.reverted events (a real bug caught by
  // this module's own revert test).
  const relevant = eventsOfTypes(excludingReverted(events), ['quote.saved']).filter(
    (e) => e.quoteId === quoteId,
  );
  if (relevant.length === 0) return { saved: false, savedEventId: null };

  // "Earliest recordedAt" per the event's doc comment; canonical (eventId)
  // order already correlates with recordedAt (UUIDv7 embeds client time,
  // ADR-0003), so the first surviving match in canonical order IS the
  // earliest -- no separate recordedAt sort needed.
  return { saved: true, savedEventId: relevant[0]!.eventId };
}

export function saveQuote(quoteId: string, ports: Ports): CommandResult<QuoteSavedEvent> {
  if (quoteId.trim().length === 0) {
    throw domainError('invalid_input', 'quoteId must not be empty');
  }

  const event: QuoteSavedEvent = {
    eventId: ports.id.nextEventId(),
    type: 'quote.saved',
    schemaVersion: 1,
    occurredOn: ports.clock.today(),
    recordedAt: ports.clock.now(),
    quoteId,
  };

  return { events: [event] };
}
